import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../context/DarkModeContext';
import { useToast } from '../../context/ToastContext';
import { useCreateCareerFocus } from '../../hooks/useCareerFocus';
import { useCareerFocusFeatures } from '../../hooks/useStatsigFeatures';
// Removed useRubric import - will handle rubric creation differently
import { supabase } from '../../utils/supabase';
import { IoCheckmarkOutline } from 'react-icons/io5';
import MappingPreview from '../Mapping/MappingPreview';
import { FocusGoal, GoalType } from '../../hooks/useGoals';
import {
  GoalList,
  RubricSelector,
  MappingInsights,
  MappingGoal,
  RubricCategory,
  SimpleMapping,
  calculateMappingInsights,
  getMappingStyles
} from '../Mapping/shared';

interface CareerFocusOnboardingProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (focusId: string) => void;
}

type SetupStep = 'basics' | 'goals' | 'rubric' | 'mapping' | 'review';
type RubricOption = 'template' | 'custom';

const STEPS: { id: SetupStep; number: number; label: string }[] = [
  { id: 'basics', number: 1, label: 'Basics' },
  { id: 'goals', number: 2, label: 'Goals' },
  { id: 'rubric', number: 3, label: 'Rubric' },
  { id: 'mapping', number: 4, label: 'Mapping' },
  { id: 'review', number: 5, label: 'Review' }
];

const PREDEFINED_GOALS = [
  { id: 'return_offer', icon: '🎯', title: 'Get the return offer', description: 'Focus on demonstrating value to secure a full-time position' },
  { id: 'skill_learning', icon: '📚', title: 'Learn specific technologies', description: 'Master new programming languages, frameworks, or tools' },
  { id: 'networking', icon: '🤝', title: 'Build professional network', description: 'Connect with colleagues and industry professionals' },
  { id: 'portfolio', icon: '💼', title: 'Build portfolio projects', description: 'Create tangible work samples for future opportunities' }
];

const TEMPLATE_OPTIONS = [
  { id: 'software_engineering', icon: '💻', name: 'Software Engineering' },
  { id: 'data_science', icon: '📊', name: 'Data Science' },
  { id: 'product_design', icon: '🎨', name: 'Product Design' },
  { id: 'product_management', icon: '📈', name: 'Product Management' },
  { id: 'marketing', icon: '📱', name: 'Marketing' },
  { id: 'finance', icon: '💰', name: 'Finance' }
];

// Helper function to get template categories
const getTemplateCategories = (templateType: string) => {
  // Common categories that appear in most templates
  const commonCategories = {
    communication: {
      name: 'Communication',
      description: 'Collaboration and communication abilities',
      weight: 20,
      criteria: [
        'Presents concepts clearly to various audiences',
        'Collaborates effectively with team members',
        'Documents work thoroughly'
      ]
    },
    growth: {
      name: 'Professional Growth',
      description: 'Learning and development',
      weight: 15,
      criteria: [
        'Seeks and incorporates feedback',
        'Shows initiative in learning',
        'Adapts to new challenges'
      ]
    }
  };

  switch (templateType) {
    case 'software_engineering':
      return [
        {
          name: 'Technical Skills',
          description: 'Core technical competencies',
          weight: 40,
          criteria: [
            'Can debug issues independently',
            'Writes clean, maintainable code',
            'Learns new technologies quickly',
            'Contributes to system design discussions'
          ]
        },
        commonCategories.communication,
        {
          name: 'Project Delivery',
          description: 'Execution and delivery capabilities',
          weight: 25,
          criteria: [
            'Meets project deadlines consistently',
            'Takes ownership of assigned features',
            'Proactively identifies and solves problems'
          ]
        },
        commonCategories.growth
      ];

    case 'data_science':
      return [
        {
          name: 'Analytical Skills',
          description: 'Data analysis and modeling expertise',
          weight: 40,
          criteria: [
            'Performs exploratory data analysis effectively',
            'Builds and validates predictive models',
            'Communicates insights through visualizations',
            'Applies statistical methods appropriately'
          ]
        },
        commonCategories.communication,
        {
          name: 'Technical Implementation',
          description: 'Coding and tool proficiency',
          weight: 25,
          criteria: [
            'Writes efficient data processing code',
            'Uses appropriate tools and frameworks',
            'Manages data pipelines effectively'
          ]
        },
        commonCategories.growth
      ];

    case 'product_design':
      return [
        {
          name: 'Design Skills',
          description: 'Core design competencies',
          weight: 40,
          criteria: [
            'Creates user-centered designs',
            'Develops high-fidelity prototypes',
            'Conducts effective user research',
            'Applies design systems consistently'
          ]
        },
        commonCategories.communication,
        {
          name: 'Problem Solving',
          description: 'Design thinking and iteration',
          weight: 25,
          criteria: [
            'Identifies user pain points effectively',
            'Iterates based on feedback',
            'Balances user needs with business goals'
          ]
        },
        commonCategories.growth
      ];

    case 'product_management':
      return [
        {
          name: 'Product Strategy',
          description: 'Strategic thinking and planning',
          weight: 35,
          criteria: [
            'Defines clear product requirements',
            'Prioritizes features based on impact',
            'Analyzes market and user data',
            'Creates effective roadmaps'
          ]
        },
        {
          name: 'Stakeholder Management',
          description: 'Cross-functional collaboration',
          weight: 30,
          criteria: [
            'Facilitates productive meetings',
            'Manages stakeholder expectations',
            'Communicates product vision clearly',
            'Builds consensus across teams'
          ]
        },
        {
          name: 'Execution',
          description: 'Delivery and implementation',
          weight: 20,
          criteria: [
            'Tracks progress against goals',
            'Removes blockers effectively',
            'Ships features on schedule'
          ]
        },
        commonCategories.growth
      ];

    case 'marketing':
      return [
        {
          name: 'Marketing Strategy',
          description: 'Campaign planning and execution',
          weight: 35,
          criteria: [
            'Develops targeted campaign strategies',
            'Analyzes campaign performance metrics',
            'Identifies and segments audiences',
            'Creates compelling messaging'
          ]
        },
        commonCategories.communication,
        {
          name: 'Content & Creative',
          description: 'Content creation and brand management',
          weight: 30,
          criteria: [
            'Produces engaging content',
            'Maintains brand consistency',
            'Optimizes for different channels'
          ]
        },
        commonCategories.growth
      ];

    case 'finance':
      return [
        {
          name: 'Financial Analysis',
          description: 'Core finance competencies',
          weight: 40,
          criteria: [
            'Performs accurate financial modeling',
            'Analyzes financial statements effectively',
            'Identifies trends and insights',
            'Makes data-driven recommendations'
          ]
        },
        commonCategories.communication,
        {
          name: 'Technical Proficiency',
          description: 'Tools and systems expertise',
          weight: 25,
          criteria: [
            'Uses financial software efficiently',
            'Automates repetitive processes',
            'Ensures data accuracy and integrity'
          ]
        },
        commonCategories.growth
      ];

    default:
      // Default to software engineering template
      return getTemplateCategories('software_engineering');
  }
};

const CareerFocusOnboarding: React.FC<CareerFocusOnboardingProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const { createFocus, isCreating } = useCreateCareerFocus();
  const { logEvent } = useCareerFocusFeatures();
  // We don't need to fetch rubrics since we're creating a new focus
  // Templates will be handled differently
  
  const [currentStep, setCurrentStep] = useState<SetupStep>('basics');
  const [setupData, setSetupData] = useState({
    // Basics
    company: '',
    role: '',
    start_date: '',
    end_date: '',
    defaultToFocusMode: true,
    
    // Goals
    selectedGoals: [] as string[],
    customGoals: [] as string[],
    
    // Rubric
    rubricOption: 'template' as RubricOption,
    selectedTemplate: 'software_engineering',
    customCategories: [] as Array<{ name: string; items: string[] }>,
    
    // Mapping
    mappings: {} as Record<string, string[]>
  });

  // Exact colors from mockup
  const bgColor = darkMode ? 'bg-[#1e293b]' : 'bg-white';
  // const modalBg = darkMode ? 'bg-[#0f172a]' : 'bg-gray-50'; // Unused
  const cardBg = darkMode ? 'bg-[#0f172a]' : 'bg-gray-50';
  const textColor = darkMode ? 'text-[#e2e8f0]' : 'text-gray-900';
  const borderColor = darkMode ? 'border-[#334155]' : 'border-gray-200';
  const mutedTextColor = darkMode ? 'text-[#94a3b8]' : 'text-gray-600';
  const subMutedColor = darkMode ? 'text-[#64748b]' : 'text-gray-500';
  const inputBg = darkMode ? 'bg-[#0f172a]' : 'bg-white';
  const activeStepBg = 'bg-[#3b82f6]';
  const completedStepBg = 'bg-[#10b981]';
  const inactiveStepBg = darkMode ? 'bg-[#334155]' : 'bg-gray-300';

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('basics');
      setSetupData({
        company: '',
        role: '',
        start_date: '',
        end_date: '',
        defaultToFocusMode: true,
        selectedGoals: [],
        customGoals: [],
        rubricOption: 'template',
        selectedTemplate: 'software_engineering',
        customCategories: [],
        mappings: {}
      });
    }
  }, [isOpen]);

  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);
  
  const canGoNext = () => {
    switch (currentStep) {
      case 'basics': 
        return setupData.company && setupData.role && setupData.start_date && setupData.end_date;
      case 'goals': 
        return setupData.selectedGoals.length > 0 || setupData.customGoals.length > 0;
      case 'rubric': 
        if (setupData.rubricOption === 'template') return setupData.selectedTemplate;
        if (setupData.rubricOption === 'custom') {
          // Check that we have at least one category with a name and at least one criterion
          return setupData.customCategories.length > 0 && 
                 setupData.customCategories.some((cat: any) => 
                   cat.name.trim() && cat.items.some((item: string) => item.trim())
                 );
        }
        return false;
      case 'mapping': 
        return true; // Mapping is optional
      case 'review': 
        return true;
      default: 
        return false;
    }
  };

  const handleNext = async () => {
    if (currentStep === 'review') {
      await finalizeSetup();
      return;
    }

    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex].id);
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex].id);
    }
  };

  const finalizeSetup = async () => {
    try {
      // Combine selected and custom goals
      
      const allGoals = [
        ...setupData.selectedGoals.map(id => {
          const goal = PREDEFINED_GOALS.find(g => g.id === id);
          return goal?.title || '';
        }),
        ...setupData.customGoals
      ].filter(Boolean);
      

      // Create the career focus with minimal settings to avoid JSON errors
      const focusData = {
        focus_type: 'internship' as const,
        industry_type: setupData.rubricOption === 'template' ? setupData.selectedTemplate : 'generic',
        start_date: setupData.start_date,
        end_date: setupData.end_date,
        company: setupData.company,
        role: setupData.role,
        // Don't store goals here since they go in a separate table
        settings: {
          defaultToFocusMode: setupData.defaultToFocusMode
        }
      };
      const result = await createFocus(focusData);

      if (result) {
        
        // STEP 1: Create rubric FIRST (before goals and mappings)
        let actualRubricCategories: Array<{ id: string; name: string; criteriaIds: string[] }> = [];
        
        if (setupData.rubricOption === 'template' && setupData.selectedTemplate) {
          try {
            // Create the rubric first
            const { data: rubricData, error: rubricError } = await supabase
              .from('internship_rubrics')
              .insert({
                user_id: result.user_id,
                focus_id: result.id,
                template_type: setupData.selectedTemplate,
                name: `${setupData.company} Internship Rubric`,
                description: `Evaluation criteria for ${setupData.role} at ${setupData.company}`,
                categories: [], // Empty array since we'll use normalized tables
                is_active: true,
                json_migrated: true // Mark as using normalized structure
              })
              .select()
              .single();

            if (rubricError) throw rubricError;

            if (rubricData) {
              
              // Get template categories
              const templateCategories = getTemplateCategories(setupData.selectedTemplate);
              
              // Insert categories and criteria
              for (let catIndex = 0; catIndex < templateCategories.length; catIndex++) {
                const cat = templateCategories[catIndex];
                
                // Insert category
                const { data: categoryData, error: catError } = await supabase
                  .from('rubric_categories')
                  .insert({
                    rubric_id: rubricData.id,
                    name: cat.name,
                    description: cat.description,
                    weight: cat.weight,
                    display_order: catIndex
                  })
                  .select()
                  .single();

                if (catError) {
                  console.error('Failed to create category:', catError);
                  continue;
                }

                if (categoryData) {
                  // Store the actual category ID for later use in mappings
                  const categoryInfo: any = {
                    id: categoryData.id,
                    name: categoryData.name,
                    criteriaIds: []
                  };
                  
                  if (cat.criteria) {
                    // Insert criteria for this category
                    const criteriaToInsert = cat.criteria.map((criterion: string, index: number) => ({
                      category_id: categoryData.id,
                      text: criterion,
                      display_order: index
                    }));

                    const { data: criteriaData, error: criteriaError } = await supabase
                      .from('rubric_criteria')
                      .insert(criteriaToInsert)
                      .select();

                    if (criteriaError) {
                      console.error('Failed to create criteria:', criteriaError);
                    } else if (criteriaData) {
                      // Store the actual criteria IDs
                      categoryInfo.criteriaIds = criteriaData.map((c: any) => c.id);
                    }
                  }
                  
                  actualRubricCategories.push(categoryInfo);
                }
              }
              
            }
          } catch (rubricError) {
            console.error('Failed to create rubric:', rubricError);
            // Don't fail the whole setup if rubric creation fails
            showToast('Focus created, but rubric setup failed. You can add it later.', 'warning');
          }
        } else if (setupData.rubricOption === 'custom' && setupData.customCategories.length > 0) {
          try {
            // Create the rubric first
            const { data: rubricData, error: rubricError } = await supabase
              .from('internship_rubrics')
              .insert({
                user_id: result.user_id,
                focus_id: result.id,
                template_type: 'custom',
                name: `${setupData.company} Custom Rubric`,
                description: `Custom evaluation criteria for ${setupData.role} at ${setupData.company}`,
                categories: [], // Empty array since we'll use normalized tables
                is_active: true,
                json_migrated: true // Mark as using normalized structure
              })
              .select()
              .single();

            if (rubricError) throw rubricError;

            if (rubricData) {
              // Filter valid categories
              const validCategories = setupData.customCategories
                .filter((cat: any) => cat.name.trim() && cat.items.some((item: string) => item.trim()));
              
              const categoryCount = validCategories.length;
              const baseWeight = Math.floor(100 / categoryCount);
              
              // Insert categories and criteria
              for (let catIndex = 0; catIndex < validCategories.length; catIndex++) {
                const cat = validCategories[catIndex];
                
                // Adjust weight for last category to ensure total is 100
                const weight = catIndex === validCategories.length - 1 
                  ? 100 - (baseWeight * (categoryCount - 1))
                  : baseWeight;
                
                // Insert category
                const { data: categoryData, error: catError } = await supabase
                  .from('rubric_categories')
                  .insert({
                    rubric_id: rubricData.id,
                    name: cat.name,
                    description: `${cat.name} evaluation criteria`,
                    weight: weight,
                    display_order: catIndex
                  })
                  .select()
                  .single();

                if (catError) {
                  console.error('Failed to create category:', catError);
                  continue;
                }

                if (categoryData) {
                  // Store the actual category ID for later use in mappings
                  actualRubricCategories.push({
                    id: categoryData.id,
                    name: categoryData.name,
                    criteriaIds: [] // Will be populated after criteria are created
                  });
                  
                  // Insert criteria for this category
                  const validItems = cat.items.filter((item: string) => item.trim());
                  const criteriaToInsert = validItems.map((criterion: string, index: number) => ({
                    category_id: categoryData.id,
                    text: criterion.trim(),
                    display_order: index
                  }));

                  const { data: criteriaData, error: criteriaError } = await supabase
                    .from('rubric_criteria')
                    .insert(criteriaToInsert)
                    .select();

                  if (criteriaError) {
                    console.error('Failed to create criteria:', criteriaError);
                  } else if (criteriaData) {
                    // Store the actual criteria IDs in the category info
                    const categoryInfo: any = actualRubricCategories.find((c: any) => c.id === categoryData.id);
                    if (categoryInfo) {
                      categoryInfo.criteriaIds = criteriaData.map((c: any) => c.id);
                    }
                  }
                }
              }
            }
          } catch (rubricError) {
            console.error('Failed to create custom rubric:', rubricError);
            showToast('Focus created, but rubric setup failed. You can add it later.', 'warning');
          }
        }
        
        // STEP 2: Create goals
        
        // Create goals in the user_focus_goals table
        if (allGoals.length > 0) {
          try {
            const goalInserts = allGoals.map((goalText, index) => {
              // Determine goal type based on selected predefined goals
              let goalType: GoalType = 'custom';
              const predefinedGoal = PREDEFINED_GOALS.find(g => g.title === goalText);
              if (predefinedGoal) {
                goalType = predefinedGoal.id as GoalType;
              }

              return {
                focus_id: result.id,
                goal_text: goalText,
                goal_type: goalType,
                priority: index + 1
              };
            });

            
            const { data: goalData, error: goalsError } = await supabase
              .from('user_focus_goals')
              .insert(goalInserts)
              .select();

            if (goalsError) {
              console.error('Failed to create goals:', goalsError);
              showToast(`Goals failed to save: ${goalsError.message}`, 'error');
              throw goalsError; // Make sure we see the error
            } else {
              showToast(`Created ${goalData.length} goals successfully`, 'success');
              
              // Save goal-rubric mappings if they exist and we have actual rubric categories
              if (setupData.mappings && Object.keys(setupData.mappings).length > 0 && goalData && actualRubricCategories.length > 0) {
                try {
                  
                  // Create a map of goal text to goal ID from the created goals
                  const goalTextToId: Record<string, string> = {};
                  goalData.forEach((goal: any) => {
                    // For predefined goals, use the goal type as the key
                    const predefinedGoal = PREDEFINED_GOALS.find(g => g.title === goal.goal_text);
                    if (predefinedGoal) {
                      goalTextToId[predefinedGoal.id] = goal.id;
                    } else {
                      // For custom goals, match by text
                      const customIndex = setupData.customGoals.indexOf(goal.goal_text);
                      if (customIndex !== -1) {
                        goalTextToId[`custom-${customIndex}`] = goal.id;
                      }
                    }
                  });
                  
                  
                  // Transform mappings to database format
                  const mappingsToInsert: any[] = [];
                  
                  Object.entries(setupData.mappings).forEach(([goalKey, rubricItems]) => {
                    const goalId = goalTextToId[goalKey];
                    if (!goalId) {
                      console.warn(`Could not find goal ID for key: ${goalKey}`);
                      return;
                    }
                    
                    // Group rubric items by category
                    const itemsByCategory: Record<string, string[]> = {};
                    
                    (rubricItems as string[]).forEach(itemId => {
                      // Parse the item ID to get category and index
                      // Format is: "category-name-index" (e.g., "technical-skills-0")
                      const parts = itemId.split('-');
                      if (parts.length >= 2) {
                        const indexStr = parts[parts.length - 1];
                        const categoryPart = parts.slice(0, -1).join('-');
                        const itemIndex = parseInt(indexStr, 10);
                        
                        // Find matching category from our actual created categories
                        actualRubricCategories.forEach((cat: any) => {
                          const catIdHyphen = cat.name.toLowerCase().replace(/\s+/g, '-');
                          
                          if (categoryPart === catIdHyphen && cat.criteriaIds && cat.criteriaIds[itemIndex]) {
                            // Use the actual category UUID
                            if (!itemsByCategory[cat.id]) {
                              itemsByCategory[cat.id] = [];
                            }
                            // Use the actual criterion ID instead of index
                            itemsByCategory[cat.id].push(cat.criteriaIds[itemIndex]);
                          }
                        });
                      }
                    });
                    
                    // Create a mapping for each category that has items
                    Object.entries(itemsByCategory).forEach(([categoryId, items]) => {
                      if (items.length > 0) {
                        mappingsToInsert.push({
                          goal_id: goalId,
                          focus_id: result.id,
                          rubric_category: categoryId,
                          rubric_items: items
                        });
                      }
                    });
                  });
                  
                  if (mappingsToInsert.length > 0) {
                    
                    const { error: mappingError } = await supabase
                      .from('goal_rubric_mappings')
                      .insert(mappingsToInsert);
                    
                    if (mappingError) {
                      console.error('Failed to save mappings:', mappingError);
                      showToast('Goal mappings could not be saved', 'warning');
                    } else {
                    }
                  }
                } catch (mappingError) {
                  console.error('Error saving mappings:', mappingError);
                  // Don't fail the whole setup if mapping creation fails
                }
              }
            }
          } catch (error) {
            console.error('Error creating goals:', error);
          }
        }
        
        logEvent('career_focus_created', {
          focus_type: 'internship',
          duration_weeks: Math.ceil(
            (new Date(setupData.end_date).getTime() - new Date(setupData.start_date).getTime()) / 
            (7 * 24 * 60 * 60 * 1000)
          ),
          goal_count: allGoals.length,
          rubric_type: setupData.rubricOption
        });
        
        showToast('Internship focus created successfully!', 'success');
        onSuccess?.(result.id);
        onClose();
      }
    } catch (error) {
      console.error('Setup error:', error);
      showToast('Failed to create internship focus', 'error');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'basics':
        return <BasicsStep setupData={setupData} setSetupData={setSetupData} />;
      case 'goals':
        return <GoalsStep setupData={setupData} setSetupData={setSetupData} />;
      case 'rubric':
        return <RubricStep setupData={setupData} setSetupData={setSetupData} />;
      case 'mapping':
        return <MappingStep setupData={setupData} setSetupData={setSetupData} />;
      case 'review':
        return <ReviewStep setupData={setupData} />;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-full p-5">
        <div className={`${bgColor} rounded-2xl max-w-[800px] w-full shadow-2xl my-8`}>
          <div className="flex flex-col max-h-[90vh]">
            {/* Fixed Header */}
            <div className="p-12 pb-0">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-6 mb-6">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className={`flex items-center gap-2 ${
                  index <= currentStepIndex ? '' : 'opacity-50'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    index < currentStepIndex 
                      ? `${completedStepBg} text-white` 
                      : index === currentStepIndex
                        ? `${activeStepBg} text-white`
                        : `${inactiveStepBg} ${subMutedColor}`
                  }`}>
                    {index < currentStepIndex ? (
                      <IoCheckmarkOutline className="w-5 h-5" />
                    ) : (
                      step.number
                    )}
                  </div>
                  <div className={`text-sm transition-colors ${
                    index <= currentStepIndex 
                      ? textColor 
                      : subMutedColor
                  }`}>
                    {step.label}
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`w-[60px] h-[2px] transition-colors ${
                    index < currentStepIndex 
                      ? completedStepBg 
                      : inactiveStepBg
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>

            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-12 py-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {renderStepContent()}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Fixed Footer */}
            <div className="flex items-center justify-between p-12 pt-0">
            <button
              onClick={currentStepIndex === 0 ? onClose : handleBack}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                darkMode 
                  ? 'bg-[#334155] hover:bg-[#475569] text-[#e2e8f0]' 
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              {currentStepIndex === 0 ? 'Cancel' : 'Back'}
            </button>

            <button
              onClick={handleNext}
              disabled={!canGoNext() || isCreating}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                canGoNext() && !isCreating
                  ? 'bg-[#3b82f6] hover:bg-[#2563eb] text-white' 
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
              }`}
            >
              {isCreating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2" />
                  Creating...
                </>
              ) : currentStep === 'review' ? (
                'Complete Setup'
              ) : (
                'Continue'
              )}
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Components
const BasicsStep: React.FC<{
  setupData: any;
  setSetupData: (data: any) => void;
}> = ({ setupData, setSetupData }) => {
  const { darkMode } = useDarkMode();
  const textColor = darkMode ? 'text-[#e2e8f0]' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-[#94a3b8]' : 'text-gray-600';
  const inputBg = darkMode ? 'bg-[#0f172a]' : 'bg-white';
  const borderColor = darkMode ? 'border-[#334155]' : 'border-gray-300';

  return (
    <div className="text-center">
      <h1 className={`text-[32px] font-semibold ${textColor} mb-4`}>
        Let's set up your internship tracking
      </h1>
      <p className={`text-lg ${mutedTextColor} mb-6 max-w-[600px] mx-auto`}>
        First, tell us about your internship details.
      </p>

      <form>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="text-left">
            <label className={`block text-sm font-medium ${textColor} mb-2`}>
              Company
            </label>
            <input
              type="text"
              value={setupData.company}
              onChange={(e) => setSetupData({ ...setupData, company: e.target.value })}
              placeholder="e.g., Google"
              className={`w-full px-4 py-3 rounded-lg border ${borderColor} ${inputBg} ${textColor} placeholder-gray-500 focus:outline-none focus:border-[#3b82f6] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] transition-all`}
            />
          </div>
          <div className="text-left">
            <label className={`block text-sm font-medium ${textColor} mb-2`}>
              Role
            </label>
            <input
              type="text"
              value={setupData.role}
              onChange={(e) => setSetupData({ ...setupData, role: e.target.value })}
              placeholder="e.g., Software Engineer Intern"
              className={`w-full px-4 py-3 rounded-lg border ${borderColor} ${inputBg} ${textColor} placeholder-gray-500 focus:outline-none focus:border-[#3b82f6] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] transition-all`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="text-left">
            <label className={`block text-sm font-medium ${textColor} mb-2`}>
              Start Date
            </label>
            <input
              type="date"
              value={setupData.start_date}
              onChange={(e) => setSetupData({ ...setupData, start_date: e.target.value })}
              className={`w-full px-4 py-3 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:border-[#3b82f6] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] transition-all ${
                darkMode ? '[color-scheme:dark]' : ''
              }`}
              style={darkMode ? { colorScheme: 'dark' } : {}}
            />
          </div>
          <div className="text-left">
            <label className={`block text-sm font-medium ${textColor} mb-2`}>
              End Date
            </label>
            <input
              type="date"
              value={setupData.end_date}
              onChange={(e) => setSetupData({ ...setupData, end_date: e.target.value })}
              className={`w-full px-4 py-3 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:border-[#3b82f6] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] transition-all ${
                darkMode ? '[color-scheme:dark]' : ''
              }`}
              style={darkMode ? { colorScheme: 'dark' } : {}}
            />
          </div>
        </div>

        <label className="flex items-center gap-3 cursor-pointer text-left">
          <input
            type="checkbox"
            checked={setupData.defaultToFocusMode}
            onChange={(e) => setSetupData({ ...setupData, defaultToFocusMode: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 text-[#3b82f6] focus:ring-[#3b82f6]"
          />
          <span className={`text-sm ${textColor}`}>
            Default to internship view when I open the app during these dates
          </span>
        </label>
      </form>
    </div>
  );
};

const GoalsStep: React.FC<{
  setupData: any;
  setSetupData: (data: any) => void;
}> = ({ setupData, setSetupData }) => {
  const { darkMode } = useDarkMode();
  const textColor = darkMode ? 'text-[#e2e8f0]' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-[#94a3b8]' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-[#0f172a]' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-[#334155]' : 'border-gray-300';
  const inputBg = darkMode ? 'bg-[#0f172a]' : 'bg-white';
  const selectedBg = darkMode ? 'bg-[#1e3a8a]' : 'bg-blue-50';

  const toggleGoal = (goalId: string) => {
    const current = setupData.selectedGoals;
    if (current.includes(goalId)) {
      setSetupData({ 
        ...setupData, 
        selectedGoals: current.filter((id: string) => id !== goalId) 
      });
    } else {
      setSetupData({ 
        ...setupData, 
        selectedGoals: [...current, goalId] 
      });
    }
  };

  const addCustomGoal = () => {
    setSetupData({
      ...setupData,
      customGoals: [...setupData.customGoals, '']
    });
  };

  const updateCustomGoal = (index: number, value: string) => {
    const updated = [...setupData.customGoals];
    updated[index] = value;
    setSetupData({ ...setupData, customGoals: updated });
  };

  const removeCustomGoal = (index: number) => {
    setSetupData({
      ...setupData,
      customGoals: setupData.customGoals.filter((_: string, i: number) => i !== index)
    });
  };

  return (
    <div className="text-center">
      <h1 className={`text-[32px] font-semibold ${textColor} mb-4`}>
        What do you want to achieve?
      </h1>
      <p className={`text-lg ${mutedTextColor} mb-6 max-w-[600px] mx-auto`}>
        Select your goals for this internship. You can choose multiple.
      </p>

      <div>
        {/* Predefined Goals */}
        <div className="grid gap-3 mb-6">
          {PREDEFINED_GOALS.map(goal => (
            <div
              key={goal.id}
              onClick={() => toggleGoal(goal.id)}
              className={`p-5 rounded-xl border-2 cursor-pointer transition-all text-left ${
                setupData.selectedGoals.includes(goal.id)
                  ? `border-[#3b82f6] ${selectedBg}` 
                  : `${cardBg} ${borderColor} hover:border-[#475569]`
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">{goal.icon}</span>
                <span className={`font-medium text-base ${textColor}`}>{goal.title}</span>
              </div>
              <div className={`text-sm ${mutedTextColor} pl-9`}>
                {goal.description}
              </div>
            </div>
          ))}
        </div>

        {/* Custom Goals */}
        {setupData.customGoals.length > 0 && (
          <div className={`border-t ${borderColor} pt-6 mb-4`}>
            <h3 className={`text-base font-medium ${textColor} mb-3 text-left`}>Custom Goals</h3>
            <div className="space-y-3">
              {setupData.customGoals.map((goal: string, index: number) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={goal}
                    onChange={(e) => updateCustomGoal(index, e.target.value)}
                    placeholder="Enter a custom goal"
                    className={`flex-1 px-4 py-3 rounded-lg border ${borderColor} ${inputBg} ${textColor} placeholder-gray-500 focus:outline-none focus:border-[#3b82f6] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] transition-all`}
                  />
                  <button
                    onClick={() => removeCustomGoal(index)}
                    className="px-4 py-3 bg-[#ef4444] hover:bg-[#dc2626] text-white rounded-lg font-medium transition-colors"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={addCustomGoal}
          className={`w-full py-3 rounded-lg border-2 border-dashed ${borderColor} ${darkMode ? 'text-[#64748b]' : 'text-gray-500'} hover:border-[#3b82f6] hover:text-[#3b82f6] transition-all text-sm`}
        >
          + Add Custom Goal
        </button>
      </div>
    </div>
  );
};

const RubricStep: React.FC<{
  setupData: any;
  setSetupData: (data: any) => void;
}> = ({ setupData, setSetupData }) => {
  const { darkMode } = useDarkMode();
  const textColor = darkMode ? 'text-[#e2e8f0]' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-[#94a3b8]' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-[#0f172a]' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-[#334155]' : 'border-gray-300';
  const selectedBg = darkMode ? 'bg-[#1e3a8a]' : 'bg-blue-50';
  const templateBg = darkMode ? 'bg-[#0f172a]' : 'bg-gray-50';

  const rubricOptions = [
    { id: 'template', icon: '🎯', title: 'Use a template', description: 'Select from industry-standard rubrics' },
    { id: 'custom', icon: '✏️', title: 'Create custom criteria', description: 'Build your own evaluation framework' }
  ];

  return (
    <div className="text-center">
      <h1 className={`text-[32px] font-semibold ${textColor} mb-4`}>
        How will you be evaluated?
      </h1>
      <p className={`text-lg ${mutedTextColor} mb-6 max-w-[600px] mx-auto`}>
        This helps us track the right evidence for your success.
      </p>

      <div>
        {/* Rubric Options */}
        <div className="grid gap-4 mb-8">
          {rubricOptions.map(option => (
            <div
              key={option.id}
              onClick={() => setSetupData({ ...setupData, rubricOption: option.id as RubricOption })}
              className={`p-6 rounded-xl border-2 cursor-pointer transition-all text-left ${
                setupData.rubricOption === option.id
                  ? `border-[#3b82f6] ${selectedBg}`
                  : `${cardBg} ${borderColor} hover:border-[#475569]`
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{option.icon}</span>
                <div>
                  <div className={`font-medium text-lg ${textColor}`}>{option.title}</div>
                  <div className={`text-sm ${mutedTextColor}`}>{option.description}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Template Selection */}
        {setupData.rubricOption === 'template' && (
          <div>
            <div className="grid grid-cols-3 gap-4">
              {TEMPLATE_OPTIONS.map(template => (
                <div
                  key={template.id}
                  onClick={() => setSetupData({ ...setupData, selectedTemplate: template.id })}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                    setupData.selectedTemplate === template.id
                      ? `border-[#3b82f6] ${selectedBg}`
                      : `${templateBg} ${borderColor} hover:border-[#475569]`
                  }`}
                >
                  <div className="text-[32px] mb-2">{template.icon}</div>
                  <div className={`text-sm font-medium ${textColor}`}>{template.name}</div>
                </div>
              ))}
            </div>

            {/* Template Preview */}
            {setupData.selectedTemplate && (
              <div className={`mt-6 p-6 ${templateBg} border ${borderColor} rounded-lg`}>
                <h3 className={`text-lg font-medium ${textColor} mb-5`}>
                  {TEMPLATE_OPTIONS.find(t => t.id === setupData.selectedTemplate)?.name} Intern Rubric
                </h3>
                <div className="grid gap-5">
                  {getTemplateCategories(setupData.selectedTemplate).map((category, index) => (
                    <div key={index} className={`${darkMode ? 'bg-[#1e293b]' : 'bg-gray-100'} rounded-md p-4`}>
                      <h4 className="text-sm font-semibold text-[#3b82f6] mb-3">{category.name}</h4>
                      <ul className={`space-y-2 text-sm ${textColor}`}>
                        {category.criteria.map((criterion, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-[#64748b] mr-2">•</span>
                            {criterion}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Custom Criteria Builder */}
        {setupData.rubricOption === 'custom' && (
          <div>
            <div className={`${templateBg} rounded-lg p-6`}>
              <p className={`text-sm ${mutedTextColor} mb-6`}>
                Create categories and add evaluation criteria for each.
              </p>
              
              {/* Categories List */}
              <div className="space-y-6">
                {setupData.customCategories.map((category: any, categoryIndex: number) => (
                  <div key={categoryIndex} className={`${cardBg} rounded-lg p-5 border ${borderColor}`}>
                    {/* Category Header */}
                    <div className="flex items-start gap-3 mb-4">
                      <input
                        type="text"
                        value={category.name}
                        onChange={(e) => {
                          const updated = [...setupData.customCategories];
                          updated[categoryIndex].name = e.target.value;
                          setSetupData({ ...setupData, customCategories: updated });
                        }}
                        placeholder="Category name (e.g., Technical Skills)"
                        className={`flex-1 px-4 py-2 rounded-lg border ${borderColor} ${darkMode ? 'bg-[#0f172a]' : 'bg-white'} ${textColor} placeholder-gray-500 focus:outline-none focus:border-[#3b82f6] transition-all`}
                      />
                      <button
                        onClick={() => {
                          const updated = setupData.customCategories.filter((_: any, i: number) => i !== categoryIndex);
                          setSetupData({ ...setupData, customCategories: updated });
                        }}
                        className="px-3 py-2 bg-[#ef4444] hover:bg-[#dc2626] text-white rounded-lg font-medium transition-colors text-sm"
                      >
                        Remove
                      </button>
                    </div>
                    
                    {/* Criteria Items */}
                    <div className="space-y-2 ml-4">
                      {category.items.map((item: string, itemIndex: number) => (
                        <div key={itemIndex} className="flex items-center gap-2">
                          <span className="text-[#64748b]">•</span>
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const updated = [...setupData.customCategories];
                              updated[categoryIndex].items[itemIndex] = e.target.value;
                              setSetupData({ ...setupData, customCategories: updated });
                            }}
                            placeholder="Enter evaluation criterion"
                            className={`flex-1 px-3 py-1.5 rounded-lg border ${borderColor} ${darkMode ? 'bg-[#0f172a]' : 'bg-white'} ${textColor} placeholder-gray-500 focus:outline-none focus:border-[#3b82f6] transition-all text-sm`}
                          />
                          <button
                            onClick={() => {
                              const updated = [...setupData.customCategories];
                              updated[categoryIndex].items = updated[categoryIndex].items.filter((_: string, i: number) => i !== itemIndex);
                              setSetupData({ ...setupData, customCategories: updated });
                            }}
                            className={`text-sm ${darkMode ? 'text-[#ef4444] hover:text-[#dc2626]' : 'text-red-600 hover:text-red-700'} transition-colors`}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const updated = [...setupData.customCategories];
                          updated[categoryIndex].items.push('');
                          setSetupData({ ...setupData, customCategories: updated });
                        }}
                        className={`text-sm ${darkMode ? 'text-[#3b82f6] hover:text-[#2563eb]' : 'text-blue-600 hover:text-blue-700'} transition-colors ml-5`}
                      >
                        + Add criterion
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Add Category Button */}
              <button
                onClick={() => {
                  setSetupData({
                    ...setupData,
                    customCategories: [...setupData.customCategories, { name: '', items: [''] }]
                  });
                }}
                className={`w-full mt-4 py-3 rounded-lg border-2 border-dashed ${borderColor} ${darkMode ? 'text-[#64748b]' : 'text-gray-500'} hover:border-[#3b82f6] hover:text-[#3b82f6] transition-all text-sm font-medium`}
              >
                + Add Category
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MappingStep: React.FC<{
  setupData: any;
  setSetupData: (data: any) => void;
}> = ({ setupData, setSetupData }) => {
  const { darkMode } = useDarkMode();
  const styles = getMappingStyles(darkMode);
  const textColor = darkMode ? 'text-[#e2e8f0]' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-[#94a3b8]' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-[#1e293b]' : 'bg-gray-50';
  const panelBg = darkMode ? 'bg-[#0f172a]' : 'bg-white';
  const borderColor = darkMode ? 'border-[#334155]' : 'border-gray-300';
  
  const [selectedGoal, setSelectedGoal] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);

  // Initialize selectedGoal
  useEffect(() => {
    if (!selectedGoal && (setupData.selectedGoals.length > 0 || setupData.customGoals.length > 0)) {
      setSelectedGoal(setupData.selectedGoals[0] || `custom-0`);
    }
  }, [setupData.selectedGoals, setupData.customGoals, selectedGoal]);

  // Get rubric items based on the selected template or rubric option
  const getRubricItems = () => {
    // If user selected a template, use that template's categories
    if (setupData.rubricOption === 'template' && setupData.selectedTemplate) {
      const templateCategories = getTemplateCategories(setupData.selectedTemplate);
      
      // Transform the template categories into the format needed for mapping
      return templateCategories.map(category => ({
        name: category.name,
        items: category.criteria.map((criterion, index) => ({
          id: `${category.name.toLowerCase().replace(/\s+/g, '-')}-${index}`,
          text: criterion
        }))
      }));
    }
    
    // If user created custom criteria, use those
    if (setupData.rubricOption === 'custom' && setupData.customCategories.length > 0) {
      return setupData.customCategories
        .filter((cat: any) => cat.name.trim() && cat.items.some((item: string) => item.trim()))
        .map((category: any) => ({
          name: category.name,
          items: category.items
            .filter((item: string) => item.trim())
            .map((item: string, index: number) => ({
              id: `${category.name.toLowerCase().replace(/\s+/g, '-')}-${index}`,
              text: item
            }))
        }));
    }
    
    // Default to software engineering template if nothing selected
    const templateCategories = getTemplateCategories('software_engineering');
    return templateCategories.map(category => ({
      name: category.name,
      items: category.criteria.map((criterion, index) => ({
        id: `${category.name.toLowerCase().replace(/\s+/g, '-')}-${index}`,
        text: criterion
      }))
    }));
  };

  const getAllGoals = () => {
    const goals: Array<{ id: string; text: string; icon: string }> = [];
    setupData.selectedGoals.forEach((goalId: string) => {
      const goal = PREDEFINED_GOALS.find(g => g.id === goalId);
      if (goal) {
        goals.push({ id: goalId, text: goal.title, icon: goal.icon });
      }
    });
    setupData.customGoals.forEach((text: string, index: number) => {
      if (text) {
        goals.push({ id: `custom-${index}`, text, icon: '✨' });
      }
    });
    return goals;
  };

  const toggleMapping = (rubricId: string) => {
    if (!selectedGoal) return;
    
    const currentMappings = setupData.mappings[selectedGoal] || [];
    const newMappings = currentMappings.includes(rubricId)
      ? currentMappings.filter((id: string) => id !== rubricId)
      : [...currentMappings, rubricId];
    
    setSetupData({
      ...setupData,
      mappings: {
        ...setupData.mappings,
        [selectedGoal]: newMappings
      }
    });
  };

  const getConnectedCount = (goalId: string) => {
    return (setupData.mappings[goalId] || []).length;
  };

  const goals = getAllGoals();
  const rubricCategories = getRubricItems();
  const currentMappings = setupData.mappings[selectedGoal] || [];
  
  // Transform data to shared component formats
  const mappingGoals: MappingGoal[] = goals.map(goal => ({
    id: goal.id,
    text: goal.text,
    type: goal.id.startsWith('custom') ? 'custom' : goal.id as MappingGoal['type']
  }));
  
  const sharedRubricCategories: RubricCategory[] = rubricCategories.map((cat: any) => ({
    id: cat.name.toLowerCase().replace(/\s+/g, '_'),
    name: cat.name,
    items: cat.items
  }));
  
  // Transform mappings to simple format for shared components
  // For onboarding, we need to match the exact item IDs
  const simpleMappings: SimpleMapping[] = [];
  Object.entries(setupData.mappings).forEach(([goalId, itemIds]) => {
    // Group items by category
    const itemsByCategory: Record<string, string[]> = {};
    (itemIds as string[]).forEach(itemId => {
      // Find which category this item belongs to
      sharedRubricCategories.forEach((cat: RubricCategory) => {
        const item = cat.items.find((item: { id: string; text: string }) => item.id === itemId);
        if (item) {
          if (!itemsByCategory[cat.id]) {
            itemsByCategory[cat.id] = [];
          }
          // Store the actual item ID
          itemsByCategory[cat.id].push(item.id);
        }
      });
    });
    
    // Create SimpleMapping for each category
    Object.entries(itemsByCategory).forEach(([categoryId, items]) => {
      simpleMappings.push({
        goalId,
        rubricCategory: categoryId,
        rubricItems: items
      });
    });
  });
  
  // Create mapping counts for GoalList
  const mappingCounts = mappingGoals.reduce((acc, goal) => {
    acc[goal.id] = getConnectedCount(goal.id);
    return acc;
  }, {} as Record<string, number>);

  // Calculate insights using shared utility
  const insights = calculateMappingInsights(sharedRubricCategories, simpleMappings);
  const selectedGoalObj = mappingGoals.find(g => g.id === selectedGoal) || null;

  // Transform data for MappingPreview component
  const focusGoals: FocusGoal[] = goals.map((goal, index) => ({
    id: goal.id,
    focus_id: 'temp',
    goal_text: goal.text,
    goal_type: goal.id.startsWith('custom') ? 'custom' : goal.id as any,
    priority: index + 1,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    evidence_count: 0
  }));

  const formattedRubricCategories = rubricCategories.map((cat: any) => ({
    id: cat.name.toLowerCase().replace(/\s+/g, '_'),
    name: cat.name,
    items: cat.items
  }));

  // Convert mappings to a simpler format for MappingPreview
  const goalRubricMappings = Object.entries(setupData.mappings).map(([goalId, rubricItems]) => {
    // Group rubric items by category
    const itemsByCategory: Record<string, string[]> = {};
    
    (rubricItems as string[]).forEach(itemId => {
      // Find which category this item belongs to
      formattedRubricCategories.forEach((cat: { id: string; name: string; items: { id: string; text: string }[] }) => {
        if (cat.items.some((item: { id: string; text: string }) => item.id === itemId)) {
          if (!itemsByCategory[cat.id]) {
            itemsByCategory[cat.id] = [];
          }
          itemsByCategory[cat.id].push(itemId);
        }
      });
    });

    // Create a mapping for each category that has items
    return Object.entries(itemsByCategory).map(([categoryId, items]) => ({
      id: `temp-${goalId}-${categoryId}`,
      goal_id: goalId,
      focus_id: 'temp',
      rubric_category: categoryId,
      rubric_items: items,
      created_at: new Date()
    }));
  }).flat();

  return (
    <div>
      <div className="text-center mb-4">
        <h1 className={`text-[32px] font-semibold ${textColor} mb-2`}>
          Connect goals to evaluation criteria
        </h1>
        <p className={`text-sm ${mutedTextColor} max-w-[600px] mx-auto`}>
          Your personal goals might not perfectly align with your company's evaluation criteria—and that's okay. This mapping helps track relevant evidence when there is overlap.
        </p>
      </div>

      {/* View Toggle - Always show button in same position */}
      <div className="flex justify-between items-center mb-6">
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className={`text-sm font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} transition-colors`}
        >
          {showPreview ? '← Back to Edit' : 'Preview Connections →'}
        </button>
        {showPreview && (
          <p className={`text-sm ${mutedTextColor}`}>
            Click on goals or criteria to highlight connections
          </p>
        )}
      </div>

      {/* Two-Panel Layout */}
      {!showPreview && (
      <div className="flex gap-6 mb-8">
        {/* Goals Panel */}
        <GoalList
          goals={mappingGoals}
          selectedGoalId={selectedGoal}
          mappingCounts={mappingCounts}
          onGoalSelect={setSelectedGoal}
          className="flex-1"
        />

        {/* Rubric Panel */}
        <RubricSelector
          categories={sharedRubricCategories}
          mappings={simpleMappings}
          selectedGoalId={selectedGoal}
          onToggleMapping={(categoryId, itemId) => {
            // itemId is already the actual item ID from the rubric
            toggleMapping(itemId);
          }}
          onSelectAll={(categoryId, selectAll) => {
            if (!selectedGoal) return;
            
            const category = sharedRubricCategories.find(cat => cat.id === categoryId);
            if (!category) return;
            
            const categoryItemIds = category.items.map((item: any) => item.id);
            const otherMappings = currentMappings.filter((id: string) => !categoryItemIds.includes(id));
            
            setSetupData({
              ...setupData,
              mappings: {
                ...setupData.mappings,
                [selectedGoal]: selectAll ? [...otherMappings, ...categoryItemIds] : otherMappings
              }
            });
          }}
          onSelectAllGlobal={(selectAll) => {
            if (!selectedGoal) return;
            
            const allItemIds = sharedRubricCategories.flatMap((cat: RubricCategory) => cat.items.map((item: { id: string; text: string }) => item.id));
            
            setSetupData({
              ...setupData,
              mappings: {
                ...setupData.mappings,
                [selectedGoal]: selectAll ? allItemIds : []
              }
            });
          }}
          className="flex-[1.5]"
        />
      </div>
      )}

      {/* Insights Section */}
      {!showPreview && (
        <MappingInsights
          insights={insights}
          selectedGoal={selectedGoalObj}
          className="mb-6"
        />
      )}

      {!showPreview && (
      <div className={`${panelBg} border ${borderColor} rounded-lg p-4`}>
        <p className={`text-sm ${textColor}`}>
          <strong>Remember:</strong> It's normal for personal goals and company evaluation criteria to not perfectly align. Focus on what matters most to you, while being aware of how you'll be evaluated.
        </p>
      </div>
      )}
      
      {/* Mapping Preview View */}
      {showPreview && (
        <MappingPreview
          goals={focusGoals}
          rubricCategories={formattedRubricCategories}
          mappings={goalRubricMappings}
          onBack={() => setShowPreview(false)}
          hideHeader={true}
        />
      )}
    </div>
  );
};

const ReviewStep: React.FC<{ setupData: any }> = ({ setupData }) => {
  const { darkMode } = useDarkMode();
  const textColor = darkMode ? 'text-[#e2e8f0]' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-[#94a3b8]' : 'text-gray-600';
  const labelColor = darkMode ? 'text-[#64748b]' : 'text-gray-500';
  const cardBg = darkMode ? 'bg-[#0f172a]' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-[#334155]' : 'border-gray-200';

  const duration = Math.ceil(
    (new Date(setupData.end_date).getTime() - new Date(setupData.start_date).getTime()) / 
    (7 * 24 * 60 * 60 * 1000)
  );

  const getAllGoals = () => {
    const goals = [];
    for (const goalId of setupData.selectedGoals) {
      const goal = PREDEFINED_GOALS.find(g => g.id === goalId);
      if (goal) goals.push(goal.title);
    }
    return [...goals, ...setupData.customGoals.filter(Boolean)];
  };

  const getRubricDescription = () => {
    if (setupData.rubricOption === 'template') {
      const template = TEMPLATE_OPTIONS.find(t => t.id === setupData.selectedTemplate);
      return `Using ${template?.name || setupData.selectedTemplate.replace(/_/g, ' ')} template`;
    }
    if (setupData.rubricOption === 'custom') {
      const categoryCount = setupData.customCategories.filter((cat: any) => 
        cat.name.trim() && cat.items.some((item: string) => item.trim())
      ).length;
      return `Using custom criteria with ${categoryCount} ${categoryCount === 1 ? 'category' : 'categories'}`;
    }
    return '';
  };

  return (
    <div className="text-center">
      <h1 className={`text-[32px] font-semibold ${textColor} mb-4`}>
        Review your internship setup
      </h1>
      <p className={`text-lg ${mutedTextColor} mb-6 max-w-[600px] mx-auto`}>
        Everything look good? You can always adjust these later.
      </p>

      <div className="space-y-6">
        {/* Basic Info */}
        <div className={`p-6 ${cardBg} rounded-lg text-left`}>
          <h3 className={`text-lg font-medium ${mutedTextColor} mb-4`}>Internship Details</h3>
          <div className="space-y-4">
            <div className="pb-4 border-b ${borderColor}">
              <div className={`text-sm ${labelColor} mb-1`}>Company</div>
              <div className={`text-base ${textColor}`}>{setupData.company}</div>
            </div>
            <div className="pb-4 border-b ${borderColor}">
              <div className={`text-sm ${labelColor} mb-1`}>Role</div>
              <div className={`text-base ${textColor}`}>{setupData.role}</div>
            </div>
            <div>
              <div className={`text-sm ${labelColor} mb-1`}>Duration</div>
              <div className={`text-base ${textColor}`}>
                {duration} weeks ({new Date(setupData.start_date).toLocaleDateString()} - {new Date(setupData.end_date).toLocaleDateString()})
              </div>
            </div>
          </div>
        </div>

        {/* Goals */}
        <div className={`p-6 ${cardBg} rounded-lg text-left`}>
          <h3 className={`text-lg font-medium ${mutedTextColor} mb-4`}>Your Goals</h3>
          <ul className="space-y-2 pl-4">
            {getAllGoals().map((goal, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-[#3b82f6] font-bold">•</span>
                <span className={`text-base ${textColor}`}>{goal}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Rubric */}
        <div className={`p-6 ${cardBg} rounded-lg text-left`}>
          <h3 className={`text-lg font-medium ${mutedTextColor} mb-4`}>Evaluation Method</h3>
          <div className={`text-base ${textColor}`}>
            {getRubricDescription()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerFocusOnboarding;